import { Box, Link, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useIndexRun } from '../../hooks/useIndexRun';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { IndexType } from '../../Types/enums';

/**
 * Displays the title of a project along with its client name (if available) and additional status information.
 *
 * @component
 *
 * @remarks
 * - If the project is currently being indexed, a loading indicator with dots is displayed.
 * - If the project has been indexed previously, the last indexing date is shown along with
 * a refresh link to re-index the project.
 */
const IndexedStatus: React.FC = () => {
  const navigate = useNavigate();

  const { currentFolder } = useSelector(selectFolders);
  const { isIndexing, latestIndexRunValues } = useIndexRun(currentFolder?.indexIds?.[0]);

  const navigateToConfigurePage = useCallback(() => {
    navigate(`/folder/${currentFolder?.id}/files/index`);
  }, [navigate, currentFolder?.id]);

  const lastIndexedLabel = latestIndexRunValues?.lastFileIndexRunTime
    ? new Date(latestIndexRunValues.lastFileIndexRunTime).toLocaleString()
    : null;

  const config = useMemo(() => {
    switch (currentFolder?.project?.indexToUse) {
      case IndexType.OPENAI:
        return {
          label: 'RAG',
          show: true,
        };

      case IndexType.GRAPH_RAG:
        return {
          label: 'Graph RAG',
          show: true,
        };

      default:
        return {
          show: false,
        };
    }
  }, [currentFolder?.project?.indexToUse]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      {config.show && (
        <Box>
          <Tooltip
            title={
              isIndexing
                ? `${config.label} indexing in progress. Please wait for it to complete before starting any new Discovery or Analysis run or using features like chat, discovery, or analysis.`
                : latestIndexRunValues?.lastFileIndexRunTime
                ? `${config.label} last indexed at ${lastIndexedLabel}. Files uploaded after this time won't be included in discovery, analysis, or chat until re-indexed.`
                : `No files have been ${config.label} indexed. Please complete the initial sync and update to unlock discovery, analysis, and chat features.`
            }
            arrow
          >
            <Typography variant="caption" color="text.secondary" sx={{ marginLeft: 1 }}>
              {isIndexing
                ? `${config.label} Indexing. `
                : latestIndexRunValues?.lastFileIndexRunTime
                ? `${config.label} last indexed at ${lastIndexedLabel}. `
                : `${config.label} not indexed yet. `}
              <Link onClick={navigateToConfigurePage} sx={{ cursor: 'pointer' }} underline="hover">
                {isIndexing
                  ? 'View index files'
                  : latestIndexRunValues?.lastFileIndexRunTime
                  ? 'Sync and update here'
                  : 'Start sync and update'}
              </Link>
            </Typography>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

export default IndexedStatus;
